import { all, call, put, select, take } from 'redux-saga/effects';
import * as actions from '../actions';
import {
  setDraftCount,
  setHasPostings,
  setHasPublishedPostings,
} from '../actions/PermanentJobs/PermanentJobs';
import { custoSetUserProperties, logCustomEvent } from '../api/analytics';
import history from '../browserHistory';
import { PermanentJobsService } from '../domain/PermanentJobs/services';
import { growthbook, shouldUseDsoStructure } from '../growthbook';
import { getShouldEnforcePermanentJobV2 } from '~/services/posthog/FeatureFlagUtils';
import { apiMethodsConst } from '../services/methods';
import { clearDatadogUser, setDatadogUser } from '../services/datadog';
import { PosthogService } from '../services/posthog';
import { AutoNavigateActionNeeded, SignUpRedesign } from '../constants/ff.posthog';
import { dashboardJobsService } from '../domain/DashboardJobs/dashboardJobsService';
import { store } from '../store/configureStore';
import { clearPosthogIdentifyCache, posthogIdentifyIfChanged } from '../utils/posthog/identify';
import {
  ACTION_NEEDED_PATH,
  DASHBOARD_PATH,
  shouldAutoNavigateToActionNeeded,
} from './helpers/shouldAutoNavigateToActionNeeded';
import { callAndDispatch } from './utils';

export function* watchInitialLoading() {
  const events = actions.createRequestTypes(actions.INITIAL_LOADING);

  while (true) {
    yield take(actions.INITIAL_LOADING);

    yield put({ type: events.REQUEST });

    // Sagas live outside React — read PostHog directly to gate legacy
    // hard-navigation paths (same pattern as growthbook's getShould* flags).
    const isSignUpRedesignOn = PosthogService.getInstance().isFeatureFlagVariant(
      SignUpRedesign,
      'test',
    );

    try {
      if (!localStorage.getItem('refresh_token_dental')) {
        throw new Error('No refresh token');
      }

      yield* callAndDispatch(apiMethodsConst.AUTH_ME_REQUEST);

      const user = yield select((state) => state.user.user);

      if (user && user.id && user.user_type !== 'DNT') {
        throw new Error('HYG not allowed');
      }

      if (user && user.id && user.is_profile_complete) {
        // Get childOffices for PostHog identification
        const childOffices = yield select((state) => state.user.childOffices);

        // Identify with PostHog (only if properties changed to avoid redundant /decide calls)
        const didIdentify = posthogIdentifyIfChanged(user, childOffices);
        if (didIdentify) {
          yield call([PosthogService.getInstance(), 'waitForFlagReload']);
        }

        // Set Datadog user context
        setDatadogUser(user);

        yield custoSetUserProperties(user);

        const selectedOfficeId = yield select((state) => state.user.selectedOfficeId);

        yield all([
          childOffices?.length > 1
            ? callAndDispatch(
                apiMethodsConst.GET_SELECTED_OFFICE_PROFILE_REQUEST,
                selectedOfficeId ?? user?.id,
              )
            : null,
          callAndDispatch(apiMethodsConst.USER_CREDENTIALS_REQUEST),
          callAndDispatch(apiMethodsConst.GET_STATE_WORKER_CLASSIFICATION_REQUEST),
        ]);

        // Pre-fetch hasPostings flag for instant ManageJobs redirect
        if (getShouldEnforcePermanentJobV2()) {
          try {
            const postingsResult = yield call(PermanentJobsService.getJobPostings, {
              limit: 1,
              page: 1,
            });
            const stats = postingsResult.statistics.tabs;
            const hasAny =
              stats.active > 0 || stats.closed > 0 || stats.draft > 0 || stats.suspended > 0;
            const hasPublished = stats.active > 0 || stats.closed > 0 || stats.suspended > 0;
            yield put(setHasPostings(hasAny));
            yield put(setHasPublishedPostings(hasPublished));
            yield put(setDraftCount(stats.draft));
          } catch {
            // Silently fail — ManageJobs will fall back to its own loading
          }
        }

        // Adjustments request
        const jobsWithAdjustments = store.getState().job.adjustments;

        if (jobsWithAdjustments.length !== 0) {
          history.push(`/dashboard/adjustments/${jobsWithAdjustments[0].id}`, {
            job_id: jobsWithAdjustments[0].id,
          });
        }

        // Experiment: guide offices straight to their Action Needed shifts on
        // login / app-open. Gated behind a flag; only fetch the count when it's
        // on. Runs after higher-priority redirects so it never overrides them
        // (the helper requires the user to still be on the dashboard).
        const isAutoNavigateActionNeededOn = PosthogService.getInstance().isFeatureEnabled(
          AutoNavigateActionNeeded,
          false,
        );

        if (isAutoNavigateActionNeededOn) {
          try {
            const dashboardJobsInfo = yield call(dashboardJobsService.getDashboardJobsInfo);

            if (
              shouldAutoNavigateToActionNeeded({
                isFlagEnabled: isAutoNavigateActionNeededOn,
                actionRequiredCount: dashboardJobsInfo?.pendingCount ?? 0,
                currentPath: history.location?.pathname,
              })
            ) {
              if (history.location?.pathname === DASHBOARD_PATH) {
                // App-open while logged in: already on the dashboard, take over now.
                history.push(ACTION_NEEDED_PATH);
              } else {
                // Fresh login: the post-login redirect lands on the dashboard a
                // tick later and would clobber an immediate push, so wait for it
                // and navigate then. The timeout drops the listener if the user
                // is routed elsewhere (e.g. incomplete profile -> sign-up).
                const stopListening = history.listen(({ location }) => {
                  if (location.pathname === DASHBOARD_PATH) {
                    stopListening();
                    history.push(ACTION_NEEDED_PATH);
                  }
                });
                setTimeout(() => stopListening(), 5000);
              }
            }
          } catch {
            // Silently fall back to the default landing page.
          }
        }
      }

      yield put({ type: events.SUCCESS });

      if (user && !user.is_profile_complete) {
        // Send incomplete-profile users back into sign-up.
        if (isSignUpRedesignOn) {
          // Hydrate WC state — step pages branch on it for the worker-type tab.
          yield* callAndDispatch(apiMethodsConst.GET_STATE_WORKER_CLASSIFICATION_REQUEST);
          // Always restart the wizard at Step 1 on refresh/boot. Mid-flow
          // navigation between steps still works because INITIAL_LOADING
          // only fires on app start, not on intra-app route changes.
          history.push('/signup/your-info');
        } else {
          history.push('/registration');
        }
      }
    } catch (error) {
      const isSignUpPath = history.location?.pathname === '/signup';

      if (isSignUpPath) {
        const hasReferralId = history.location?.search?.includes('referral');

        if (hasReferralId) {
          history.push(`/${history.location?.search}`);
        } else {
          history.push('/');
        }
      }

      if (
        history.location?.pathname !== '/signup' &&
        !history.location?.pathname?.includes('/forgotpassword') &&
        !history.location.search.includes('referral')
      ) {
        history.push('/');
      }

      if (error?.message === 'HYG not allowed') {
        yield put({
          type: 'LOGOUT_ACTION_SUCCESS',
          payload: {
            isError: true,
            title: 'Sorry!',
            text: 'Only dental offices are allowed to use the Dental Portal. Please, download the GoTu mobile application.',
            buttonText: 'Close',
            action: 'LOGOUT_ACTION',
          },
        });
      }
      if (error.response?.data.message !== 'No refresh token') {
        yield put({ type: events.FAILURE, payload: error.response?.data });
      }
      yield put({ type: events.FAILURE });
    }
  }
}

export function* watchSignIn() {
  const events = actions.createRequestTypes(actions.SIGN_IN_ACTION);
  while (true) {
    const cred = yield take(actions.SIGN_IN_ACTION);

    yield put({ type: events.REQUEST });

    try {
      yield* callAndDispatch(apiMethodsConst.SIGN_IN_REQUEST, cred);

      yield logCustomEvent('sign_in', {
        email: cred.email,
      });

      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

export function* watchSignUP() {
  const events = actions.createRequestTypes(actions.SIGN_UP_ACTION);
  while (true) {
    const cred = yield take(actions.SIGN_UP_ACTION);
    yield put({ type: events.REQUEST });

    try {
      yield* callAndDispatch(apiMethodsConst.AUTH_SIGN_UP_REQUEST, cred);
      yield* callAndDispatch(
        apiMethodsConst.AUTH_REFRESH_REQUEST,
        localStorage.getItem('refresh_token_dental'),
      );
      yield* callAndDispatch(apiMethodsConst.AUTH_ME_REQUEST);

      if (shouldUseDsoStructure()) {
        yield* callAndDispatch(apiMethodsConst.ACCEPT_TERMS_AND_CONDITIONS_FOR_STAFF_REQUEST);
        yield* callAndDispatch(apiMethodsConst.GET_TERMS_AND_CONDITIONS_FOR_STAFF_REQUEST);
      } else {
        yield* callAndDispatch(apiMethodsConst.ACCEPT_TERMS_REQUEST);
      }
      yield logCustomEvent('sign_up_started', {
        email: cred.email,
      });
      yield put({ type: events.SUCCESS });

      const user = yield select((state) => state.user.user);

      cred?.onSuccess?.(user);

      localStorage.setItem(`showPrimaryOfficeContactPopup-${user.id}`, 'false');

      history.push('/registration');
    } catch (error) {
      localStorage.removeItem('refresh_token_dental');
      localStorage.removeItem('selectedOfficeId');
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

export function* watchAuthLogout() {
  const events = actions.createRequestTypes(actions.LOGOUT_ACTION);
  while (true) {
    yield take(actions.LOGOUT_ACTION);
    yield put({ type: events.REQUEST });
    try {
      // Capture the id before the API call so we can purge this user's identify cache.
      const userId = yield select((state) => state.user?.user?.id);

      yield* callAndDispatch(apiMethodsConst.AUTH_LOGOUT_REQUEST);
      growthbook.setAttributes({});

      localStorage.removeItem('referralId');

      // Clear Datadog user context
      clearDatadogUser();

      // Reset PostHog identity so the next login does not merge Persons via the cookie's
      // residual distinct_id. Pair the cached-identify wipe so the next session re-identifies.
      PosthogService.getInstance().reset();
      clearPosthogIdentifyCache(userId);

      history.push('/');
      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

